import React from 'react';
import { ArrowRight, Building2, Layers3 } from 'lucide-react';

import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { formatCurrencyAmount } from '../../lib/currency';
import { EmptyState } from '../common/States';

const SuperadminSubscriptionsPanel = ({ summary, rows, planMix, onNavigate }) => {
  return (
    <Card className="animate-slide-up overflow-hidden border-primary/15 bg-gradient-to-br from-card via-card to-primary/5">
      <CardHeader className="border-b border-border">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Layers3 className="h-4 w-4 text-primary" />
              Subscriptions
            </CardTitle>
            <CardDescription>Portfolio-wide plan coverage and tenant mix</CardDescription>
          </div>
          <button
            type="button"
            onClick={() => onNavigate?.('billing/subscriptions')}
            className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
          >
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-5 pt-5">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs font-medium text-muted-foreground">Active Subscriptions</p>
            <p className="mt-2 text-2xl font-semibold text-foreground">{summary.active_subscriptions || 0}</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs font-medium text-muted-foreground">MRR</p>
            <p className="mt-2 text-2xl font-semibold text-foreground">{formatCurrencyAmount(summary.total_mrr || 0, 'INR')}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {(planMix || []).slice(0, 4).map((row) => (
            <Badge key={row.name} variant="outline">
              {row.name}: {row.value}
            </Badge>
          ))}
        </div>

        <div className="space-y-3">
          {(rows || []).slice(0, 5).map((row, index) => (
            <div
              key={row.tenant_id}
              className="animate-slide-up rounded-xl border border-border bg-card p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/20 hover:shadow-sm"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-foreground">{row.tenant_name}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{row.plan_name} • {row.billing_account_name}</p>
                </div>
                <Badge variant={row.subscription_status === 'active' ? 'success' : 'outline'}>
                  {row.subscription_status}
                </Badge>
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Monthly Revenue</span>
                <span className="font-semibold text-foreground">{formatCurrencyAmount(row.monthly_revenue || 0, row.currency_code || 'INR')}</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Outstanding</span>
                <span className="font-semibold text-warning">{formatCurrencyAmount(row.outstanding_amount || 0, row.currency_code || 'INR')}</span>
              </div>
            </div>
          ))}
          {(!rows || rows.length === 0) && (
            <EmptyState
              compact
              icon={Building2}
              title="No subscriptions available"
              description="Tenant subscriptions will appear here once plans are assigned."
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SuperadminSubscriptionsPanel;
